import React from 'react';
import styled from '@emotion/styled';
import { colors, fontSize } from 'global/variables';
import { getUtcDateFormatted } from 'utils/timeFormatHelpers';
import withTranslation from 'hoc/withTranslation';
import { SquareRechargeIcon } from 'icons/EVIcons';

const { secondaryTextColor, secondaryMarkerColor, white } = colors;
const { small } = fontSize;

const StyledStopDetails = styled.div({
  lineHeight: '1rem',
  padding: '0.2rem',
  color: secondaryTextColor,
  fontSize: small,
  backgroundColor: white,
  paddingBottom: '0.5rem',
  animation: 'EntryAnimation .3s 1',
  animationTimingFunction: 'ease-out',
  fontWeight: '400',
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
});

const StyledJobId = styled.span({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
});

const StyledStopDetailsSpanHeader = styled.div({
  paddingTop: '0.6rem',
  paddingBottom: '0',
  textTransform: 'uppercase',
  textAlign: 'left',
  width: '100%',
  color: secondaryMarkerColor,
  animation: 'EntryAnimationHeader .3s 1 !important',
  animationTimingFunction: 'ease-out',
  fontWeight: '400',
  fontSize: small,
});

const StyledIconWrapper = styled.span({
  display: 'inline-flex',
  width: '18px',
  height: '18px',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  '& svg': {
    width: '18px',
    height: '18px',
  },
});

const formatWalkDistance = (meters) => {
  if (meters === null || meters === undefined) return '–';
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)}km`;
  return `${Math.round(meters)}m`;
};

const ActivityTypeIcon = ({ type }) => {
  const color = secondaryTextColor;

  if (type === 'delivery') {
    return (
      <StyledIconWrapper>
        <svg viewBox="0 0 16 16" fill="none">
          <path d="M8 2L8 11" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
          <path
            d="M4 8L8 13L12 8"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </StyledIconWrapper>
    );
  }
  if (type === 'pickup') {
    return (
      <StyledIconWrapper>
        <svg viewBox="0 0 16 16" fill="none">
          <path d="M8 14L8 5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
          <path
            d="M4 8L8 3L12 8"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </StyledIconWrapper>
    );
  }
  if (
    type === 'break' ||
    type === 'drivingRestTime' ||
    type === 'workingRestTime' ||
    type === 'dutyWorkingRestTime' ||
    type === 'dutyDrivingRestTime'
  ) {
    return (
      <StyledIconWrapper>
        <svg viewBox="0 5 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="8" width="12" height="9" rx="1.5" stroke={color} strokeWidth="1.5" />
          <path
            d="M16 10.5C18.5 10.5 18.5 14.5 16 14.5"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path d="M7 6C7 6 7.8 4 8.5 6" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
          <path
            d="M11 6C11 6 11.8 4 12.5 6"
            stroke={color}
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      </StyledIconWrapper>
    );
  }
  if (type === 'reload') {
    return (
      <StyledIconWrapper>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M20 8A9 9 0 1 0 20.5 13"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M20 4V8.5H15.5"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </StyledIconWrapper>
    );
  }
  if (type === 'recharge') {
    return (
      <StyledIconWrapper>
        <SquareRechargeIcon />
      </StyledIconWrapper>
    );
  }
  if (type === 'pudo') {
    return (
      <StyledIconWrapper>
        <svg viewBox="3 3 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 4L19 8L19 16L12 20L5 16L5 8Z"
            stroke={color}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M19 8L12 12L5 8"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M12 12L12 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </StyledIconWrapper>
    );
  }
  return (
    <span style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.02em', color }}>
      {type ? type.slice(0, 3) : '–'}
    </span>
  );
};

const StopDetails = ({
  stop,
  offset,
  translations: {
    tourSolution: {
      activityHeaders: { id, arrival, type, walk },
    },
  },
}) => {
  const { activities } = stop;

  return (
    <>
      <StyledStopDetails>
        <StyledStopDetailsSpanHeader>{id}</StyledStopDetailsSpanHeader>
      </StyledStopDetails>
      <StyledStopDetails>
        <StyledStopDetailsSpanHeader>{arrival}</StyledStopDetailsSpanHeader>
      </StyledStopDetails>
      <StyledStopDetails style={{ paddingLeft: '2rem' }}>
        <StyledStopDetailsSpanHeader>{type}</StyledStopDetailsSpanHeader>
      </StyledStopDetails>
      <StyledStopDetails>
        <StyledStopDetailsSpanHeader>{walk}</StyledStopDetailsSpanHeader>
      </StyledStopDetails>
      <StyledStopDetails />
      {activities &&
        activities.map((activity, i) => (
          <React.Fragment key={i}>
            <StyledStopDetails data-test-id="StopDetailId">
              <StyledJobId title={activity.jobId || '–'}>{activity.jobId || '–'}</StyledJobId>
            </StyledStopDetails>
            <StyledStopDetails data-test-id="StopDetailArrival">
              {getUtcDateFormatted(activity.time ? activity.time.start : stop.time.arrival, offset)}
            </StyledStopDetails>
            <StyledStopDetails data-test-id="StopDetailType" style={{ paddingLeft: '2rem' }}>
              <ActivityTypeIcon type={activity.type} />
            </StyledStopDetails>
            <StyledStopDetails data-test-id="StopDetailWalk">
              {formatWalkDistance(stop.intraStopDistance)}
            </StyledStopDetails>
            <StyledStopDetails />
          </React.Fragment>
        ))}
    </>
  );
};

export default withTranslation(StopDetails);
